import React, { useState, useEffect, useContext } from "react";
import api from "../axios/api";
import {
  Box,
  Typography,
  Modal,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
} from "@mui/material";
import { GlobalContext } from "../contexts/GlobalContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Button from '../components/common/styled components/Button';
import SortIcon from '@mui/icons-material/Sort';
import SearchIcon from '@mui/icons-material/Search';

const itemsPerPage = 10;

const PointRedemptionPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [availablePoints, setAvailablePoints] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [redemptionRequests, setRedemptionRequests] = useState([]);
  const [pointsToClaim, setPointsToClaim] = useState("");
  const [userName, setUserName] = useState("");
  const { role } = useContext(GlobalContext);
  const [page, setPage] = useState(1);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  //search sort and filter
  const [searchTerm, setSearchTerm] = useState("");
  const [searchFilter, setSearchFilter] = useState("requestCode");
  const [sortOrder, setSortOrder] = useState("desc");

  const filteredAndSortedRequests = redemptionRequests
    .filter((request) => {
      if (searchFilter === "user") {
        return request.full_name
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      } else if (searchFilter === "requestCode") {
        return request.request_code
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      } else if (searchFilter === "status") {
        const status =
          request.request_status === 1
            ? "success"
            : request.request_status === -1
              ? "rejected"
              : "new request";
        return status.toLowerCase().includes(searchTerm.toLowerCase());
      }
      return true;
    })
    .sort((a, b) => {
      if (sortOrder === "asc") {
        return new Date(a.created_at) - new Date(b.created_at);
      } else {
        return new Date(b.created_at) - new Date(a.created_at);
      }
    });

  const displayValue = (value) => value || "-";

  const getPaginatedData = () => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedRequests.slice(startIndex, endIndex);
  };

  const paginatedActivities = getPaginatedData();

  const totalPages = Math.ceil(filteredAndSortedRequests.length / itemsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  useEffect(() => {
    fetchPointRedemptionData();
  }, []);

  const fetchPointRedemptionData = async () => {
    try {
      const response = await api.get("/api/points/getpointredemptionData", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const { Requests, Points } = response.data;
      setRedemptionRequests(Requests);
      setAvailablePoints(Points[0].collected_points);
      setUserName(Points[0].full_name);
    } catch (error) {
      toast.error("Failed to fetch point redemption data");
    }
  };

  const handleClaimClick = () => {
    setIsModalOpen(true);
  };

  const handleConfirmRedemption = async () => {
    const pointsToClaimNumber = parseInt(pointsToClaim, 10);

    if (
      !pointsToClaim ||
      pointsToClaim <= 0 ||
      pointsToClaim > availablePoints
    ) {
      toast.error("Points exceed current balance");
      return;
    }

    setIsLoading(true);
    try {
      await api.post(
        "/api/points/redeemMyPoints",
        { RequestedPoints: pointsToClaimNumber },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setIsLoading(false);
      setIsModalOpen(false);
      toast.success(
        "Your Redemption request created successfully! Our team will respond within 24 hrs."
      );
      fetchPointRedemptionData();
      setPointsToClaim("");
    } catch (error) {
      setIsLoading(false);
      setIsModalOpen(false);
      toast.error(error.response?.data?.message || "Failed to redeem points");
    }
  };

  const handleApprove = async (reqid) => {
    try {
      await api.post(
        "/api/admin/ApproveClaimRequest",
        { reqid },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("Request approved successfully");
      fetchPointRedemptionData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to approve request");
    }
  };

  const handleReject = async (reqid, rejectionReason) => {
    try {
      await api.post(
        "/api/admin/RejectClaimRequest",
        { reqid, rejectionReason },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("Request rejected successfully");
      fetchPointRedemptionData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to reject request");
    }
  };

  const getStatusColor = (status) => {
    if (status === 1) return "success";
    if (status === -1) return "error";
    return "warning";
  };

  const getStatusText = (status) => {
    if (status === 1) return "Success";
    if (status === -1) return "Rejected";
    return "New Request";
  };

  const renderMobileCard = (request) => (
    <Card sx={{ mb: 2, boxShadow: 2 }} key={request.id}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {displayValue(request.request_code)}
          </Typography>
          <Chip
            label={getStatusText(request.request_status)}
            color={getStatusColor(request.request_status)}
            size="small"
            sx={{ fontSize: '0.75rem' }}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <Typography variant="body2">
            <strong>User:</strong> {displayValue(request.full_name)}
          </Typography>

          <Typography variant="body2">
            <strong>Points:</strong> {displayValue(request.point_approved)} / {displayValue(request.point_requested)}
          </Typography>

          <Typography variant="body2">
            <strong>Date:</strong>{' '}
            {displayValue(
              request.created_at ? new Date(request.created_at).toLocaleString() : null
            )}
          </Typography>
        </Box>


        {request.request_status === -1 && (
          <Typography variant="body2" color="error">
            <strong>Reason:</strong> {request.rejection_reason}
          </Typography>
        )}

        {role === "admin" && request.request_status === 0 && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              onClick={() => handleApprove(request.id)}
              sx={{ fontSize: '0.8rem', py: 0.5 }}
            >
              Approve
            </Button>

            <FormControl
              size="small"
              sx={{ minWidth: 120, fontSize: '0.8rem' }}
            >
              <InputLabel id={`reject-reason-label-mobile-${request.id}`}>
                Reject Reason
              </InputLabel>
              <Select
                labelId={`reject-reason-label-mobile-${request.id}`}
                defaultValue=""
                size="small"
                onChange={(e) => handleReject(request.id, e.target.value)}
              >
                <MenuItem value="" disabled>Select Reason</MenuItem>
                <MenuItem value="Reason A">Reason A</MenuItem>
                <MenuItem value="Reason B">Reason B</MenuItem>
                <MenuItem value="Reason C">Reason C</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const toggleSearch = () => {
    setIsSearchExpanded(!isSearchExpanded);
  };

  return (
    <div>
      <Box sx={{ width: "100%", margin: "auto", padding: isMobile ? 2 : 3 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          mb: 2
        }}>
          <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
            Current Balance: {availablePoints} points ({userName})
          </Typography>

          <Button
            onClick={handleClaimClick}
            sx={{
              mt: isMobile ? 1 : 0,
              width: isMobile ? '100%' : 'auto',
            }}
          >
            Create Redeem Request
          </Button>
        </Box>

        {isMobile ? (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: isSearchExpanded ? 2 : 0 }}>
              <IconButton onClick={toggleSearch} color="primary">
                <SearchIcon />
              </IconButton>

              <IconButton
                onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                color="primary"
              >
                <SortIcon sx={{ transform: sortOrder === "asc" ? 'rotate(180deg)' : 'none' }} />
              </IconButton>
            </Box>

            {isSearchExpanded && (
              <Box sx={{ mt: 1 }}>
                <TextField
                  label="Search"
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ width: '100%', mb: 1 }}
                  multiline={true}
                />
                <FormControl variant="outlined" size="small" sx={{ width: '100%' }}>
                  <InputLabel>Search By</InputLabel>
                  <Select
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    label="Search By"
                  >
                    <MenuItem value="requestCode">Request Code</MenuItem>
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="status">Status</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ display: "flex", gap: 2, marginBottom: 2 }}>
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flexGrow: 1 }}
              rows={1}
              multiline={true}
            />



            <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Search By</InputLabel>
              <Select
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                label="Search By"
              >
                <MenuItem value="requestCode">Request Code</MenuItem>
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="status">Status</MenuItem>
              </Select>
            </FormControl>
            <Button
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            >
              Sort {sortOrder === "asc" ? "↑" : "↓"}
            </Button>
          </Box>
        )}

        {isMobile ? (
          <Box>
            {paginatedActivities.length > 0 ?
              paginatedActivities.map(request => renderMobileCard(request)) :
              <Typography variant="body1" sx={{ textAlign: 'center', my: 3 }}>
                No requests found
              </Typography>
            }
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: "#002F43" }}>
                <TableRow>
                  <TableCell sx={{ color: "#fff" }}>Request Code</TableCell>
                  <TableCell sx={{ color: "#fff" }}>User</TableCell>
                  <TableCell sx={{ color: "#fff" }}>
                    Approved / Requested Points
                  </TableCell>
                  <TableCell sx={{ color: "#fff" }}>Status</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Requested At</TableCell>
                  {role === "admin" && (
                    <TableCell sx={{ color: "#fff" }}>Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedActivities.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{displayValue(request.request_code)}</TableCell>
                    <TableCell>{displayValue(request.full_name)}</TableCell>
                    <TableCell>
                      {`${displayValue(request.point_approved)} / ${displayValue(
                        request.point_requested
                      )}`}
                    </TableCell>

                    <TableCell>
                      {request.request_status === 1 && (
                        <span style={{ color: "green" }}>Success</span>
                      )}
                      {request.request_status === -1 && (
                        <>
                          <span style={{ color: "red" }}>Rejected</span>
                          <div style={{ color: "red" }}>
                            ({request.rejection_reason})
                          </div>
                        </>
                      )}
                      {request.request_status === 0 && (
                        <span style={{ color: "#FFDE21" }}>New Request</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {displayValue(
                        request.created_at
                          ? new Date(request.created_at).toLocaleString()
                          : null
                      )}
                    </TableCell>
                    {role === "admin" ? (
                      request.request_status === 0 ? (
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              gap: "5px",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Button
                              onClick={() => handleApprove(request.id)}
                            >
                              Approve
                            </Button>

                            <FormControl
                              sx={{
                                width: 80,
                                marginBottom: "10px",
                                display: "inline-flex",
                                padding: "0px",
                              }}
                              size="small"
                            >
                              <InputLabel
                                id={`reject-reason-label-${request.id}`}
                                sx={{
                                  fontSize: 12,
                                  paddingBottom: 4,
                                  textAlign: "center",
                                  height: "5px",
                                }}
                              >
                                Reject Reason
                              </InputLabel>
                              <Select
                                labelId={`reject-reason-label-${request.id}`}
                                defaultValue=""
                                size="small"
                                onChange={(e) =>
                                  handleReject(request.id, e.target.value)
                                }
                              >
                                <MenuItem value="" disabled>
                                  Select Reason
                                </MenuItem>
                                <MenuItem value="Reason A">Reason A</MenuItem>
                                <MenuItem value="Reason B">Reason B</MenuItem>
                                <MenuItem value="Reason C">Reason C</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                        </TableCell>
                      ) : (
                        <TableCell
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            fontSize: "24px",
                          }}
                        >
                          -
                        </TableCell>
                      )
                    ) : (
                      <></>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {totalPages > 1 && (
          <Box sx={{
            display: "flex",
            justifyContent: "center",
            marginTop: 2,
            '& .MuiPaginationItem-root': {
              fontSize: isMobile ? '0.75rem' : '0.875rem',
            }
          }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handleChangePage}
              color="primary"
              size={isMobile ? "small" : "medium"}
            />
          </Box>
        )}

        <Modal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          aria-labelledby="redemption-modal"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: isMobile ? "90%" : 400,
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 3,
              borderRadius: 1,
            }}
          >
            <Typography variant="h6" component="h2" gutterBottom>
              Redeem Points
            </Typography>
            <TextField
              fullWidth
              type="number"
              label="Points to Redeem *"
              value={pointsToClaim}
              onChange={(e) => {
                const value = e.target.value;
                if (
                  value === "" ||
                  (/^\d*$/.test(value) && !value.includes("."))
                ) {
                  setPointsToClaim(value);
                }
              }}
              sx={{ mt: 2 }}
              required
            />

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Button
                onClick={() => setIsModalOpen(false)}
                sx={{ mr: 1 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmRedemption}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : "Confirm"}
              </Button>
            </Box>
          </Box>
        </Modal>
        <ToastContainer
          closeOnClick
          position={isMobile ? "bottom-center" : "top-right"}
          autoClose={3000}
        />
      </Box>
    </div>
  );
};

export default PointRedemptionPage;
